import { useEffect, useState } from 'react'

/**
 * HOW MANY GRID COLUMNS ACTUALLY FIT, RIGHT NOW.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THE DEFECT THIS EXISTS FOR, AND IT IS THE WORST ONE FOUND IN THIS SEQUENCE.
 *
 * §4.2's cap was the constant 6, derived at a 16px root: six cells of 48px plus
 * five 4px gaps fit a 358px column. Correct there, and wrong everywhere else —
 * because the cell floor is `3rem` and SCALES with her text while the content
 * column does not.
 *
 * At a 200% system font the cells no longer fit five across, so the cell grid
 * WRAPPED to a second line, three then two. The sticky header did not wrap.
 * Measured on a live N=5 grid:
 *
 *   header labels at x =  65, 119, 172, 226, 279
 *   answer cells  at x =  65, 154, 244,  65, 154   <- wrapped
 *
 * Column 4's label sits at 226 and its cell sits at 65, underneath column 1.
 * So the grid silently re-maps every label to the wrong answer: she reads
 * "Most of the time", taps the circle beneath it, and the app stores "At no
 * time". A validated instrument, an ordinary accessibility setting, and no
 * visible sign that anything is wrong.
 *
 * That is not a layout bug. It is the control lying about what it is recording,
 * and it corrupts the study's data rather than merely looking wrong.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * THE FIX IS THE RULE ALREADY THERE, EVALUATED AGAINST THE LIVE ROOT rather
 * than frozen at one. When it fails, the section falls back to §4.1's stacked
 * rows — a form that already exists, is already proven, and is exactly what a
 * 7-option section does today.
 *
 *   N * cellMin + (N - 1) * gap  <=  contentWidth
 *
 * Everything in that inequality except the viewport is expressed in rem, so all
 * of it is read from the live root:
 *
 *   cellMin  3rem     the 48px target floor (§4.2)
 *   gap      0.25rem  --s-1
 *   content  min(viewport, 34rem-ish cap) minus the two --s-4 gutters
 *
 * Reproduces every measured figure: N<=6 at 16px, N<=5 at 20px, N<=3 at 32px.
 */

const CELL_MIN_REM = 3 // §4.2's 48px floor at a 16px root
const GAP_REM = 0.25 // --s-1
const GUTTER_REM = 1 // --s-4, one each side
const MAX_WIDTH_EM = 34 // the Screen body's cap

/** The largest N whose cells fit one row at the CURRENT text size. */
export function gridCapacity(rootPx, viewportPx) {
  const root = Number(rootPx) > 0 ? Number(rootPx) : 16
  const viewport = Number(viewportPx) > 0 ? Number(viewportPx) : 390
  const content = Math.min(viewport, MAX_WIDTH_EM * root) - 2 * GUTTER_REM * root
  const cell = CELL_MIN_REM * root
  const gap = GAP_REM * root
  // N*cell + (N-1)*gap <= content  ->  N <= (content + gap) / (cell + gap)
  const n = Math.floor((content + gap) / (cell + gap))
  return Math.max(0, n)
}

function measure() {
  if (typeof window === 'undefined') return { rootPx: 16, viewportPx: 390 }
  const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
  const viewportPx = document.documentElement.clientWidth || window.innerWidth || 390
  return { rootPx, viewportPx }
}

/**
 * Live capacity, re-measured when the viewport or the root font changes.
 *
 * A raised system font size reflows the document, so `resize` covers it on
 * every browser this app runs in. It is deliberately NOT cached across renders:
 * a stale capacity is exactly the failure being fixed, one layer up.
 */
export function useGridCapacity() {
  const [{ rootPx, viewportPx }, set] = useState(measure)

  useEffect(() => {
    const update = () => set(measure())
    update()
    window.addEventListener('resize', update)

    /*
     * A 1rem PROBE, because the obvious observers do not fire.
     *
     * Raising the root font size does NOT change documentElement's box and does
     * NOT emit `resize`, so observing either of those left the capacity frozen
     * at whatever it was on mount — the grid kept rendering at 200% and the
     * alignment check caught it still wrapping onto three lines.
     *
     * An element sized in `rem` DOES change when the root font changes, so its
     * box is the only reliable signal available without polling. It is inert:
     * off-screen, aria-hidden, no pointer events, and it participates in
     * nothing except being measured.
     */
    let observer = null
    let probe = null
    if (typeof ResizeObserver !== 'undefined' && typeof document !== 'undefined') {
      probe = document.createElement('div')
      probe.setAttribute('aria-hidden', 'true')
      probe.style.cssText =
        'position:absolute;width:1rem;height:1rem;top:-9999px;left:-9999px;pointer-events:none'
      document.body.appendChild(probe)
      observer = new ResizeObserver(update)
      observer.observe(probe)
    }

    return () => {
      window.removeEventListener('resize', update)
      observer?.disconnect()
      probe?.remove()
    }
  }, [])

  return gridCapacity(rootPx, viewportPx)
}
